import { useEffect, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'
import { Link } from 'react-router-dom'
import {
  AudioLines, Bell, BellRing, Brain, Calendar, CheckSquare, ChevronDown,
  FileText, Globe, Mail, MessageCircle, Mic, Repeat, Search, ShieldCheck,
  Sparkles, Sunrise, Undo2, Zap,
} from 'lucide-react'
import type { PlanKey } from '../api/client'
import { fmtRub, fmtNum, perMonth, usePricing } from '../hooks/usePricing'

const TG_BOT_URL = 'https://t.me/DeloAppBot'
const SUPPORT_URL = 'https://t.me/zakharovsemyon'

// Пробный период выдаётся в backend/app/bot/handlers/onboarding.py (TRIAL_DAYS)
// и приглашённым по реферальной ссылке (2 дня referral + 1 день за онбординг).
// Число дублируется здесь так же, как в текстах бота (app/bot/i18n/ru.py).
const TRIAL_DAYS = 3

// ── Контент ───────────────────────────────────────────────────────────────────

/** Боли, с которых начинается разговор. Ровно то, что Delo закрывает функциями. */
const PAINS = [
  'Договорились в чате — вспомнил, когда уже поздно',
  'Встреча названа голосом, а в календарь никто не занёс',
  'Письмо прочитал с телефона и забыл ответить',
]

/** Что умеет — только то, что реально есть в коде (services/tools.py, limits.py). */
const FEATURES: {
  icon: typeof Calendar
  title: string
  text: string
  tier?: 'Plus' | 'Pro'
}[] = [
  {
    icon: Calendar,
    title: 'Календарь',
    text: 'Скажи «встреча с Андреем во вторник в 15:00» — запишу. Спрошу, когда ты свободен, и найду окно.',
  },
  {
    icon: Repeat,
    title: 'Google и Apple Календарь',
    text: 'Двусторонняя синхронизация: событие из Delo появляется там, а изменённое или удалённое снаружи доезжает обратно. Напоминания и задачи со сроком тоже уходят в календарь.',
    tier: 'Plus',
  },
  {
    icon: BellRing,
    title: 'Предупреждение о начале',
    text: 'За 15 минут до встречи напишу в чат — и о своей, и о той, что ты завёл в Apple или Google. События на весь день — утром.',
  },
  {
    icon: Bell,
    title: 'Напоминания',
    text: 'На время, на «когда проснусь», повторяющиеся — каждый день, по будням, раз в неделю или в месяц.',
  },
  {
    icon: CheckSquare,
    title: 'Задачи',
    text: 'Со сроком и приоритетом. Задача со сроком встаёт в календарь, а выполненная оттуда уходит — вечер снова свободен.',
  },
  {
    icon: Brain,
    title: 'Память',
    text: 'Помню, где ты работаешь, как зовут коллег и о чём вы договорились. Всё видно в приложении, любой факт можно удалить.',
  },
  {
    icon: Mic,
    title: 'Голос',
    text: 'Отправь голосовое — расшифрую и сделаю. Отвечу тоже голосом, если удобнее слушать.',
  },
  {
    icon: Mail,
    title: 'Gmail и Яндекс Почта',
    text: '«Что важного во входящих?», «Ответь Ивану, что согласен на пятницу» — прочитаю, найду, отвечу за тебя.',
    tier: 'Plus',
  },
  {
    icon: Globe,
    title: 'Поиск в интернете',
    text: 'Курсы, погода, новости, расписания — когда нужен ответ по свежим данным, а не по памяти.',
    tier: 'Plus',
  },
  {
    icon: FileText,
    title: 'Файлы и документы',
    text: 'Пришли PDF, DOCX, TXT или CSV — прочитаю и отвечу по содержимому. Попроси таблицу или договор — соберу файл и отправлю в чат.',
    tier: 'Plus',
  },
  {
    icon: MessageCircle,
    title: 'Telegram-чаты',
    text: 'Читаю выбранные тобой чаты, замечаю договорённости и дедлайны, предлагаю напоминание.',
    tier: 'Pro',
  },
  {
    icon: Undo2,
    title: 'Удалённое и изменённое',
    text: 'Собеседник удалил сообщение или переписал — пришлю копию в личку: из какого чата, от кого, что было и что стало. Вместе с фото, голосовыми и файлами.',
    tier: 'Pro',
  },
  {
    icon: AudioLines,
    title: 'Расшифровка в переписке',
    text: 'Голосовые и кружки превращаются в текст прямо в чате — и твои, чтобы собеседник прочитал, и чужие, чтобы не включать звук.',
    tier: 'Pro',
  },
  {
    icon: Sunrise,
    title: 'Утренний разбор',
    text: 'В 8:00 — что сегодня горит, какие встречи и где окна между ними. Собирается по твоему дню, а не шаблоном.',
    tier: 'Pro',
  },
  {
    icon: Search,
    title: 'Договорённости в почте',
    text: 'Сам просматриваю новые входящие и предлагаю поставить напоминание, если в письме есть срок.',
    tier: 'Pro',
  },
]

/** Демо-переписка в hero: то, ради чего человек и приходит. */
const DEMO_SCRIPT: { user: string; delo: string }[] = [
  {
    user: 'Запиши встречу с Андреем во вторник в 15:00',
    delo: 'Записал: вторник, 15:00. Добавил в Google Календарь — на это время у тебя свободно.',
  },
  {
    user: 'Напомни, когда проснусь, позвонить маме',
    delo: 'Напомню, как только ты появишься утром.',
  },
  {
    user: 'Что важного в почте?',
    delo: 'Два письма ждут ответа. Ирина просит смету до пятницы — поставить напоминание на четверг?',
  },
]

/** Ещё примеры фраз — короткие, для сканирования глазами. */
const PHRASES = [
  'Напомни завтра в 10 позвонить в клинику',
  'Что у меня на этой неделе?',
  'Запомни: клиенты в основном из e-commerce',
  'Найди письма от Ивана за месяц',
  'Собери таблицу расходов за июль в Excel',
  'Когда я свободен для часовой встречи?',
]

/** Проактивность Pro — главное отличие тарифа, продаём отдельным блоком. */
const PROACTIVE = [
  {
    icon: MessageCircle,
    title: 'Замечаю договорённости',
    text: 'Обещания, дедлайны и встречи в чатах и во входящих письмах.',
  },
  {
    icon: Bell,
    title: 'Спрашиваю, а не решаю сам',
    text: 'Предлагаю напоминание — ты подтверждаешь одной кнопкой.',
  },
  {
    icon: Zap,
    title: 'Отвечаю, когда ты занят',
    text: 'Автоответчик в выбранных чатах, в твоей манере. Можно с подтверждением.',
  },
]

/** Тарифы. Цены — только с бэкенда; лимиты совпадают с backend/app/services/limits.py. */
const TIERS: {
  id: 'free' | 'plus' | 'pro'
  name: string
  tagline: string
  month: PlanKey | null
  year: PlanKey | null
  popular?: boolean
  features: string[]
}[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Посмотреть, как это работает',
    month: null,
    year: null,
    features: [
      '5 сообщений в день, из них 2 голосовых',
      'Календарь, 5 напоминаний и 5 задач',
      'Предупрежу за 15 минут до события',
      'Память на 20 фактов',
      'Один голосовой ответ в день',
      'Вечерний чекин в 20:00 — что осталось и что завтра',
    ],
  },
  {
    id: 'plus',
    name: 'Plus',
    tagline: 'Ты просишь — Delo делает',
    month: 'plus',
    year: 'plus_yearly',
    features: [
      '40 сообщений в день, из них 10 голосовых',
      'Gmail и Яндекс Почта: читать, искать, отвечать',
      'Google и Apple Календарь — синхронизация в обе стороны',
      'События и задачи со сроком сами уезжают в твой календарь',
      'Поиск в интернете и разбор файлов',
      'Память на 300 фактов, до 100 напоминаний и задач',
      '20 голосовых ответов в месяц',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Delo работает само',
    month: 'pro',
    year: 'pro_yearly',
    popular: true,
    features: [
      'Всё из Plus',
      'Читаю выбранные Telegram-чаты и нахожу договорённости',
      'Покажу, что в чате удалили или переписали',
      'Расшифрую голосовые и кружки прямо в переписке',
      'Сам проверяю входящие письма и предлагаю напоминания',
      'Утренний разбор дня в 8:00',
      'Автоответчик в чатах, когда ты занят',
      '300 сообщений в день, из них 100 голосовых',
      'Напоминания, задачи и файлы без ограничений',
    ],
  },
]

const PRIVACY_POINTS = [
  {
    icon: ShieldCheck,
    title: 'Доступ — только через OAuth',
    text: 'Пароль от почты Delo не видит. Отключить можно в один клик — в приложении или в настройках Google.',
  },
  {
    icon: MessageCircle,
    title: 'Чаты — только те, что ты выбрал',
    text: 'История до подключения не читается. Голосовые и кружки разбираю, только если сам включишь — по умолчанию выключено.',
  },
  {
    icon: FileText,
    title: 'Тексты не хранятся целиком',
    text: 'Письмо или сообщение живёт внутри одной проверки; остаётся только результат — напоминание или факт. Исключение одно: если включить показ удалённых, копии из выбранных чатов лежат 30 дней и удаляются сами.',
  },
  {
    icon: Zap,
    title: 'Данные забираются и удаляются',
    text: 'Выгрузка всего одним файлом, удаление аккаунта — командой /delete.',
  },
]

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'Что такое Delo?',
    a: 'Личный ассистент в Telegram. Помогает с делами и вопросами, запоминает важное, ведёт календарь и напоминает вовремя — голосом или текстом. Работает в переписке с ботом, а списки, календарь и настройки живут в мини-приложении внутри Telegram.',
  },
  {
    q: 'Нужно ли что-то устанавливать?',
    a: 'Нет. Delo живёт прямо в Telegram — открой @DeloAppBot и напиши первое сообщение. Регистрации, паролей и отдельного приложения нет.',
  },
  {
    q: 'Это бесплатно?',
    a: `При первом входе даю ${TRIAL_DAYS} дня Pro — полный доступ, без карты. Дальше можно остаться на бесплатном тарифе: 5 сообщений в день, календарь, напоминания, задачи и память. Платные тарифы — когда этого станет мало.`,
  },
  {
    q: 'Чем Pro отличается от Plus?',
    a: 'На Plus Delo делает то, о чём ты просишь: находит письмо, добавляет встречу, отвечает на вопрос. На Pro Delo работает сам — читает выбранные чаты и новые входящие письма, находит договорённости и дедлайны, предлагает напоминания без запроса. Плюс утренний разбор дня, автоответчик, расшифровка голосовых прямо в переписке и копии удалённых и изменённых сообщений.',
  },
  {
    q: 'Как работает чтение чатов?',
    a: 'Подключение делается через Telegram Business: Настройки → Telegram Business → Чат-боты → @DeloAppBot, и там же ты выбираешь чаты. Delo видит только новые сообщения в них, находит договорённости и сроки и предлагает напоминание — решение всегда за тобой. История до подключения не обрабатывается. Нужен Telegram Premium и тариф Pro.',
  },
  {
    q: 'Что ещё Delo умеет в чатах?',
    a: 'Три вещи, и каждая включается отдельно в настройках — по умолчанию всё выключено. Расшифровка голосовых и кружков: твоих, чтобы собеседник мог прочитать, и чужих, чтобы ты не включал звук. Копии удалённых и изменённых сообщений — приходят только тебе, в личный чат с Delo, вместе с вложениями; в общий чат ничего не возвращается. И автоответчик, который отвечает за тебя, когда ты занят.',
  },
  {
    q: 'Что Delo хранит из моих чатов?',
    a: 'По умолчанию — ничего целиком: сообщение обрабатывается, чтобы найти договорённость, и остаётся только результат. Если включить показ удалённых и изменённых, копии сообщений из выбранных чатов хранятся 30 дней и затем удаляются сами. Отключил бота от чатов в Telegram — копии исчезают вместе с подключением.',
  },
  {
    q: 'Безопасно ли подключать почту?',
    a: 'Доступ выдаётся по официальному протоколу OAuth, токены хранятся в зашифрованном виде, пароль от ящика Delo не знает. Полные тексты писем нигде не сохраняются: письмо обрабатывается, чтобы найти нужное, и остаётся только результат. Отключить можно в любой момент.',
  },
  {
    q: 'Что с календарём?',
    a: 'Delo ведёт свой календарь и синхронизирует его с Google Calendar и Apple Calendar в обе стороны. Скажи голосом, что и когда, — добавлю. Туда же уезжают напоминания и задачи со сроком, так что всё, у чего есть время, видно в одном месте. За 15 минут до начала — предупреждение в чат, о событиях из Apple и Google тоже.',
  },
  {
    q: 'Как платить и есть ли автопродление?',
    a: 'Картой или Telegram Stars — прямо в чате. Автопродления нет: подписка не списывается сама, Delo напомнит, когда срок будет подходить к концу. Годовой тариф дешевле двенадцати месячных.',
  },
  {
    q: 'Что будет, когда подписка закончится?',
    a: 'Тариф станет бесплатным, данные останутся на месте — память, напоминания и задачи никуда не денутся. Подключённые сервисы отключатся, но их можно вернуть после продления.',
  },
  {
    q: 'Можно вернуть деньги?',
    a: 'Да, в течение 14 дней после оплаты, если подписка не использовалась. Напиши на my@zakharovsem.ru — рассмотрим в течение 10 дней. Подробности в оферте.',
  },
  {
    q: 'Delo может ошибаться?',
    a: 'Да. Ответы формируются автоматически и могут содержать неточности. В вопросах медицины, права и финансов нужен специалист, а не ассистент.',
  },
]

// ── Стили ─────────────────────────────────────────────────────────────────────
// Одна таблица стилей вместо inline-обработчиков: так работают :hover, :focus и
// медиазапросы, а раскладка перестраивается без слушателя resize в JS.

const CSS = `
.lp {
  --bg: #08080C;
  --surface: #101017;
  --surface-2: #15151F;
  --line: rgba(122,146,255,0.12);
  --line-strong: rgba(122,146,255,0.28);
  --ink: #EDEDF5;
  --ink-1: rgba(237,237,245,0.66);
  --ink-2: rgba(237,237,245,0.42);
  --accent: #5B7FFF;
  --accent-2: #4A6CF7;
  --accent-soft: rgba(91,127,255,0.12);
  background: var(--bg);
  color: var(--ink);
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100dvh;
}
.lp *, .lp *::before, .lp *::after { box-sizing: border-box; }
.lp :where(p) { margin: 0; }

.lp-wrap { width: 100%; max-width: 1080px; margin: 0 auto; padding: 0 20px; }
.lp-narrow { max-width: 680px; margin: 0 auto; }
.lp-section { padding: 64px 0; scroll-margin-top: 76px; }
.lp-section--tight { padding: 40px 0; }

.lp-serif {
  font-family: 'Instrument Serif', Georgia, serif;
  font-weight: 400;
  letter-spacing: -0.01em;
}
.lp-h1 { font-size: clamp(34px, 8vw, 60px); line-height: 1.04; margin: 0 0 18px; }
.lp-h2 { font-size: clamp(26px, 5vw, 40px); line-height: 1.12; margin: 0 0 14px; }
.lp-h3 { font-size: 15px; font-weight: 600; margin: 0 0 6px; letter-spacing: -0.01em; }
.lp-lead { font-size: clamp(15px, 3.6vw, 18px); line-height: 1.55; color: var(--ink-1); }
.lp-label {
  font-size: 11px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--accent); margin: 0 0 12px;
}
.lp-muted { color: var(--ink-2); font-size: 13px; line-height: 1.55; }
.lp-center { text-align: center; }

/* ── Шапка ── */
.lp-nav {
  position: sticky; top: 0; z-index: 30;
  background: rgba(8,8,12,0.72);
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid transparent;
  transition: border-color 200ms ease;
}
.lp-nav.is-stuck { border-bottom-color: var(--line); }
.lp-nav-inner { display: flex; align-items: center; justify-content: space-between; height: 60px; gap: 12px; }
.lp-nav-brand { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 600; }
.lp-nav-links { display: none; gap: 24px; font-size: 14px; }
.lp-nav-links a { color: var(--ink-1); text-decoration: none; transition: color 150ms ease; }
.lp-nav-links a:hover { color: var(--ink); }
@media (min-width: 860px) { .lp-nav-links { display: flex; } }

/* ── Кнопки ── */
.lp-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  border: none; border-radius: 14px; cursor: pointer; text-decoration: none;
  font-family: inherit; font-size: 16px; font-weight: 600; padding: 15px 30px;
  background: linear-gradient(135deg, #6C8CFF 0%, var(--accent-2) 100%);
  color: #fff; box-shadow: 0 8px 30px rgba(74,108,247,0.32);
  transition: transform 140ms ease, box-shadow 140ms ease;
  white-space: nowrap;
}
.lp-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 38px rgba(74,108,247,0.46); }
.lp-btn:active { transform: translateY(0); }
.lp-btn--sm { font-size: 14px; padding: 10px 18px; border-radius: 11px; box-shadow: none; }
.lp-btn--ghost {
  background: transparent; color: var(--ink-1);
  border: 1px solid var(--line-strong); box-shadow: none;
}
.lp-btn--ghost:hover { color: var(--ink); box-shadow: none; background: rgba(122,146,255,0.06); }
.lp-btn--wide { width: 100%; }
.lp :focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; }

/* ── Hero ── */
.lp-hero { position: relative; padding: 48px 0 8px; text-align: center; overflow: hidden; }
.lp-hero-glow {
  position: absolute; top: -80px; left: 50%; transform: translateX(-50%);
  width: min(760px, 130vw); height: 520px; pointer-events: none; z-index: 0;
  background: radial-gradient(circle at 50% 35%, rgba(91,127,255,0.20) 0%, transparent 68%);
}
.lp-hero-inner { position: relative; z-index: 1; }
.lp-badge {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 7px 14px; border-radius: 999px; margin-bottom: 22px;
  background: var(--accent-soft); border: 1px solid var(--line-strong);
  font-size: 13px; font-weight: 500; color: #C3CEFF;
}
.lp-hero-title { background: linear-gradient(165deg, #FFFFFF 0%, rgba(237,237,245,0.78) 100%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.lp-hero-lead { max-width: 520px; margin: 0 auto 28px; }
.lp-cta-row { display: flex; flex-direction: column; align-items: center; gap: 12px; }
@media (min-width: 560px) { .lp-cta-row { flex-direction: row; justify-content: center; } }
.lp-cta-note { font-size: 13px; color: var(--ink-2); margin-top: 14px; }
@media (min-width: 960px) {
  .lp-hero { padding: 72px 0 32px; text-align: left; }
  .lp-hero-inner { display: grid; grid-template-columns: 1.02fr 0.98fr; gap: 52px; align-items: center; }
  .lp-hero-inner > .lp-narrow { margin: 0; max-width: 560px; }
  .lp-hero-lead { margin-left: 0; margin-right: 0; }
  .lp-cta-row { justify-content: flex-start; }
  .lp-chat { margin: 0; max-width: 100%; }
}

/* ── Демо-переписка ── */
.lp-chat {
  max-width: 460px; margin: 40px auto 0; text-align: left;
  background: linear-gradient(180deg, var(--surface) 0%, #0C0C13 100%);
  border: 1px solid var(--line); border-radius: 22px; padding: 18px 16px 20px;
  box-shadow: 0 30px 80px rgba(0,0,0,0.55);
  min-height: 380px; display: flex; flex-direction: column;
}
.lp-chat-body { margin-top: auto; }
.lp-chat-head {
  display: flex; align-items: center; gap: 10px;
  padding-bottom: 14px; margin-bottom: 14px; border-bottom: 1px solid var(--line);
}
.lp-chat-name { font-size: 14px; font-weight: 600; }
.lp-chat-status { font-size: 11px; color: var(--ink-2); }
.lp-msg {
  max-width: 86%; padding: 10px 14px; font-size: 14px; line-height: 1.5;
  margin-bottom: 10px; animation: lpMsgIn 320ms ease both;
}
.lp-msg--user {
  margin-left: auto; color: #fff;
  background: linear-gradient(135deg, #5273F0 0%, var(--accent-2) 100%);
  border-radius: 16px 16px 4px 16px;
}
.lp-msg--delo {
  margin-right: auto; background: var(--surface-2); color: var(--ink);
  border: 1px solid var(--line); border-radius: 16px 16px 16px 4px;
}
.lp-typing { display: inline-flex; gap: 5px; align-items: center; }
.lp-typing span {
  width: 6px; height: 6px; border-radius: 50%; background: var(--ink-2);
  animation: lpDot 1.3s infinite;
}
.lp-typing span:nth-child(2) { animation-delay: 0.18s; }
.lp-typing span:nth-child(3) { animation-delay: 0.36s; }

/* ── Полоса доверия ── */
.lp-strip {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;
  border-top: 1px solid var(--line); border-bottom: 1px solid var(--line);
  padding: 18px 0; margin-top: 48px;
}
@media (min-width: 720px) { .lp-strip { grid-template-columns: repeat(4, 1fr); } }
.lp-strip-item { display: flex; align-items: center; gap: 9px; font-size: 13px; color: var(--ink-1); }

/* ── Карточки ── */
.lp-grid { display: grid; gap: 12px; grid-template-columns: 1fr; }
@media (min-width: 640px) { .lp-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 960px) { .lp-grid--3 { grid-template-columns: repeat(3, 1fr); } }
.lp-card {
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 16px; padding: 18px;
  transition: border-color 180ms ease, transform 180ms ease;
}
.lp-card:hover { border-color: var(--line-strong); transform: translateY(-2px); }
.lp-card-text { font-size: 13.5px; line-height: 1.55; color: var(--ink-1); }
.lp-icon {
  width: 36px; height: 36px; border-radius: 10px; margin-bottom: 12px;
  background: var(--accent-soft); display: flex; align-items: center; justify-content: center;
}
.lp-card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.lp-tier {
  font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  padding: 3px 7px; border-radius: 6px; color: #C3CEFF;
  background: var(--accent-soft); border: 1px solid var(--line-strong);
}

/* ── Боли ── */
.lp-pain {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 14px 16px; border-radius: 14px;
  background: var(--surface); border: 1px solid var(--line);
  font-size: 14px; line-height: 1.5; color: var(--ink-1);
}
.lp-pain-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); margin-top: 7px; flex-shrink: 0; }

/* ── Фразы ── */
.lp-phrases { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.lp-phrase {
  font-size: 13.5px; padding: 9px 14px; border-radius: 999px;
  background: var(--surface); border: 1px solid var(--line); color: var(--ink-1);
}

/* ── Тарифы ── */
.lp-switch {
  display: inline-flex; padding: 4px; border-radius: 12px; margin: 0 auto 28px;
  background: var(--surface); border: 1px solid var(--line);
}
.lp-switch button {
  border: none; background: transparent; cursor: pointer; font-family: inherit;
  font-size: 13.5px; font-weight: 600; color: var(--ink-2);
  padding: 8px 16px; border-radius: 9px; transition: background 160ms ease, color 160ms ease;
}
.lp-switch button.is-on { background: var(--accent-soft); color: var(--ink); }
.lp-plans { display: grid; gap: 14px; grid-template-columns: 1fr; }
@media (min-width: 900px) { .lp-plans { grid-template-columns: repeat(3, 1fr); } }
.lp-plan {
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 20px; padding: 22px; display: flex; flex-direction: column; gap: 14px;
  position: relative;
}
.lp-plan--popular {
  border-color: var(--line-strong);
  background: linear-gradient(180deg, rgba(91,127,255,0.07) 0%, var(--surface) 55%);
  box-shadow: 0 18px 50px rgba(74,108,247,0.14);
}
.lp-plan-flag {
  position: absolute; top: -11px; left: 22px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 999px; color: #fff;
  background: linear-gradient(135deg, #6C8CFF 0%, var(--accent-2) 100%);
}
.lp-plan-name { font-size: 18px; font-weight: 600; }
.lp-plan-tagline { font-size: 13px; color: var(--ink-2); margin-top: 2px; }
.lp-plan-price { font-size: 30px; font-weight: 700; letter-spacing: -0.02em; }
.lp-plan-price small { font-size: 14px; font-weight: 500; color: var(--ink-2); }
.lp-plan-note { font-size: 12.5px; color: var(--ink-2); margin-top: 4px; min-height: 18px; }
.lp-plan-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 9px; }
.lp-plan-list li { display: flex; gap: 9px; font-size: 13.5px; line-height: 1.5; color: var(--ink-1); }
.lp-check { color: var(--accent); flex-shrink: 0; margin-top: 3px; }
.lp-skel {
  display: inline-block; width: 110px; height: 30px; border-radius: 8px;
  background: linear-gradient(90deg, var(--surface-2) 25%, #1D1D28 50%, var(--surface-2) 75%);
  background-size: 200% 100%; animation: lpShimmer 1.4s infinite;
}

/* ── FAQ ── */
.lp-faq { border: 1px solid var(--line); border-radius: 16px; overflow: hidden; background: var(--surface); }
.lp-faq-item + .lp-faq-item { border-top: 1px solid var(--line); }
.lp-faq-q {
  width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 14px;
  padding: 16px 18px; background: transparent; border: none; cursor: pointer;
  font-family: inherit; font-size: 14.5px; font-weight: 600; color: var(--ink); text-align: left;
  transition: background 160ms ease;
}
.lp-faq-q:hover { background: rgba(122,146,255,0.05); }
.lp-faq-chevron { flex-shrink: 0; color: var(--accent); transition: transform 200ms ease; }
.lp-faq-chevron.is-open { transform: rotate(180deg); }
.lp-faq-a { padding: 0 18px 18px; font-size: 13.5px; line-height: 1.65; color: var(--ink-1); animation: lpFade 200ms ease; }

/* ── Финальный блок и подвал ── */
.lp-final {
  text-align: center; padding: 56px 24px; border-radius: 24px;
  background: linear-gradient(180deg, rgba(91,127,255,0.12) 0%, var(--surface) 70%);
  border: 1px solid var(--line-strong);
}
.lp-footer { border-top: 1px solid var(--line); padding: 26px 0 40px; }
@media (max-width: 859px) { .lp-footer { padding-bottom: calc(96px + env(safe-area-inset-bottom)); } }
.lp-footer-row {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 8px 14px; font-size: 13px; color: var(--ink-2);
}
.lp-footer-row a { color: var(--ink-2); text-decoration: none; transition: color 150ms ease; }
.lp-footer-row a:hover { color: var(--ink); }

/* ── Липкая кнопка на мобильном ── */
.lp-sticky {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 40;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: rgba(8,8,12,0.9); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid var(--line);
  transform: translateY(110%); transition: transform 260ms ease;
}
.lp-sticky.is-on { transform: translateY(0); }
@media (min-width: 860px) { .lp-sticky { display: none; } }

/* ── Появление при скролле ── */
[data-reveal] { opacity: 0; transform: translateY(18px); transition: opacity 420ms cubic-bezier(0.23, 1, 0.32, 1), transform 420ms cubic-bezier(0.23, 1, 0.32, 1); }
[data-reveal].is-in { opacity: 1; transform: none; }

@media (prefers-reduced-motion: no-preference) { html { scroll-behavior: smooth; } }

@keyframes lpFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes lpMsgIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
@keyframes lpDot { 0%, 60%, 100% { opacity: 0.25 } 30% { opacity: 1 } }
@keyframes lpShimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }

@media (prefers-reduced-motion: reduce) {
  .lp *, .lp *::before, .lp *::after {
    animation-duration: 0.01ms !important; animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  [data-reveal] { opacity: 1; transform: none; }
}
`

// ── Мелкие компоненты ─────────────────────────────────────────────────────────

function TelegramIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.19 13.982l-2.963-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.961.577z" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="lp-check" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function Logo({ size = 32, decorative }: { size?: number; decorative?: boolean }) {
  return (
    <img
      src="/icon-192.png"
      alt={decorative ? '' : 'Delo — логотип личного ассистента в Telegram'}
      width={size}
      height={size}
      style={{
        width: size, height: size, borderRadius: size / 3.4, objectFit: 'cover', flexShrink: 0,
        boxShadow: `0 0 ${size}px rgba(74,108,247,0.35)`,
      }}
    />
  )
}

function Cta({ children, sm, ghost, wide, href = TG_BOT_URL }: {
  children: ReactNode; sm?: boolean; ghost?: boolean; wide?: boolean; href?: string
}) {
  const cls = ['lp-btn', sm && 'lp-btn--sm', ghost && 'lp-btn--ghost', wide && 'lp-btn--wide']
    .filter(Boolean).join(' ')
  return (
    <a className={cls} href={href}>
      {!ghost && <TelegramIcon size={sm ? 15 : 18} />}
      {children}
    </a>
  )
}

function Section({ id, label, title, lead, children, tight }: {
  id?: string; label?: string; title?: string; lead?: string; children: ReactNode; tight?: boolean
}) {
  return (
    <section id={id} className={tight ? 'lp-section lp-section--tight' : 'lp-section'}>
      <div className="lp-wrap">
        {(label || title || lead) && (
          <div className="lp-narrow lp-center" data-reveal style={{ marginBottom: 32 }}>
            {label && <p className="lp-label">{label}</p>}
            {title && <h2 className="lp-serif lp-h2">{title}</h2>}
            {lead && <p className="lp-lead">{lead}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  )
}

// ── Хуки ──────────────────────────────────────────────────────────────────────

/** Один наблюдатель на всю страницу: элементу с data-reveal вешает .is-in. */
function useReveal() {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))
    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach(n => n.classList.add('is-in'))
      return
    }
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in')
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    )
    nodes.forEach(n => io.observe(n))
    return () => io.disconnect()
  }, [])
}

/** Позиция скролла: липкая кнопка снизу и подчёркивание шапки. */
function useScrolled(offset: number) {
  const [past, setPast] = useState(false)
  useEffect(() => {
    const onScroll = () => setPast(window.scrollY > offset)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [offset])
  return past
}

/**
 * Проигрывает демо-переписку: реплика пользователя → «печатает» → ответ Delo.
 * Стартует, когда карточка появилась в кадре, чтобы человек увидел анимацию,
 * а не её хвост.
 */
function useChatPlayback(ref: RefObject<HTMLElement>) {
  // Шаг = индекс в плоском списке «реплика/ответ»: 0 — ничего, дальше по одному.
  const [step, setStep] = useState(0)
  const [typing, setTyping] = useState(false)
  const [started, setStarted] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node || started) return
    if (typeof IntersectionObserver === 'undefined') { setStarted(true); return }
    const io = new IntersectionObserver(
      entries => { if (entries.some(e => e.isIntersecting)) { setStarted(true); io.disconnect() } },
      { threshold: 0.3 },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [ref, started])

  useEffect(() => {
    if (!started) return
    const total = DEMO_SCRIPT.length * 2
    const timers: number[] = []
    let delay = 400
    for (let i = 0; i < total; i++) {
      const isDeloTurn = i % 2 === 1
      if (isDeloTurn) {
        timers.push(window.setTimeout(() => setTyping(true), delay))
        delay += 900
        timers.push(window.setTimeout(() => { setTyping(false); setStep(i + 1) }, delay))
        delay += 1400
      } else {
        timers.push(window.setTimeout(() => setStep(i + 1), delay))
        delay += 800
      }
    }
    return () => timers.forEach(clearTimeout)
  }, [started])

  return { step, typing }
}

// ── Страница ──────────────────────────────────────────────────────────────────

export function Landing() {
  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const [period, setPeriod] = useState<'month' | 'year'>('month')
  const chatRef = useRef<HTMLDivElement>(null)

  useReveal()
  const scrolled = useScrolled(24)
  const pastHero = useScrolled(560)
  const { step, typing } = useChatPlayback(chatRef)

  // Цены — только с бэкенда (backend/app/tariffs.py через /api/subscription/pricing).
  const pricing = usePricing()

  return (
    <div className="lp">
      <style>{CSS}</style>

      {/* ── Шапка ── */}
      <header className={scrolled ? 'lp-nav is-stuck' : 'lp-nav'}>
        <div className="lp-wrap lp-nav-inner">
          <span className="lp-nav-brand">
            <Logo size={28} />
            Delo
          </span>
          <nav className="lp-nav-links">
            <a href="#features">Возможности</a>
            <a href="#proactive">Работает само</a>
            <a href="#pricing">Тарифы</a>
            <a href="#faq">Вопросы</a>
          </nav>
          <Cta sm>Открыть в Telegram</Cta>
        </div>
      </header>

      <main>
        {/* ── Hero ── */}
        <section className="lp-hero">
          <div className="lp-hero-glow" />
          <div className="lp-wrap lp-hero-inner">
            <div className="lp-narrow">
              <span className="lp-badge">
                <Sparkles size={14} />
                {TRIAL_DAYS} дня Pro бесплатно — без карты
              </span>
              <h1 className="lp-serif lp-h1 lp-hero-title">Больше ничего не теряется</h1>
              <p className="lp-lead lp-hero-lead">
                Личный ассистент в Telegram. Скажи голосом или напиши — запишу встречу,
                поставлю напоминание, отвечу на письмо. Подключи почту и чаты —
                сам замечу договорённости и дедлайны.
              </p>
              <div className="lp-cta-row">
                <Cta>Открыть в Telegram</Cta>
                <a className="lp-btn lp-btn--ghost" href="#features">Что умеет</a>
              </div>
              <p className="lp-cta-note">Без установки и регистрации · Отключить можно в любой момент</p>
            </div>

            {/* Демо-переписка */}
            <div className="lp-chat" ref={chatRef}>
              <div className="lp-chat-head">
                <Logo size={34} decorative />
                <div>
                  <div className="lp-chat-name">Delo</div>
                  <div className="lp-chat-status">в сети</div>
                </div>
              </div>
              <div className="lp-chat-body">
                {DEMO_SCRIPT.map((pair, i) => (
                  <div key={i}>
                    {step > i * 2 && <div className="lp-msg lp-msg--user">{pair.user}</div>}
                    {step > i * 2 + 1 && <div className="lp-msg lp-msg--delo">{pair.delo}</div>}
                    {typing && step === i * 2 + 1 && (
                      <div className="lp-msg lp-msg--delo" aria-label="Delo печатает">
                        <span className="lp-typing"><span /><span /><span /></span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Полоса доверия ── */}
        <div className="lp-wrap">
          <div className="lp-strip" data-reveal>
            <div className="lp-strip-item"><TelegramIcon size={16} /> Работает внутри Telegram</div>
            <div className="lp-strip-item"><Mic size={16} color="#5B7FFF" /> Голосом и текстом</div>
            <div className="lp-strip-item"><ShieldCheck size={16} color="#5B7FFF" /> Доступы через OAuth</div>
            <div className="lp-strip-item"><Zap size={16} color="#5B7FFF" /> Настройка за минуту</div>
          </div>
        </div>

        {/* ── Боли ── */}
        <Section
          tight
          label="Знакомо?"
          title="Дела теряются не потому, что их много"
          lead="А потому, что они записаны в трёх разных местах — и ни одно из них не напомнит вовремя."
        >
          <div className="lp-narrow" style={{ display: 'grid', gap: 10 }}>
            {PAINS.map((p, i) => (
              <div className="lp-pain" key={i} data-reveal style={{ transitionDelay: `${i * 70}ms` }}>
                <span className="lp-pain-dot" />
                {p}
              </div>
            ))}
          </div>
          <p className="lp-narrow lp-center lp-lead" data-reveal style={{ marginTop: 24 }}>
            Delo — одно место, куда можно сказать что угодно. И оно само разложит это по календарю,
            напоминаниям и задачам.
          </p>
        </Section>

        {/* ── Возможности ── */}
        <Section
          id="features"
          label="Возможности"
          title="Что умеет Delo"
          lead="Всё это работает в обычной переписке — командам учиться не нужно."
        >
          <div className="lp-grid lp-grid--3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon
              return (
                <div className="lp-card" key={f.title} data-reveal style={{ transitionDelay: `${(i % 3) * 70}ms` }}>
                  <div className="lp-icon"><Icon size={18} color="#5B7FFF" /></div>
                  <div className="lp-card-head">
                    <h3 className="lp-h3" style={{ margin: 0 }}>{f.title}</h3>
                    {f.tier && <span className="lp-tier">{f.tier}</span>}
                  </div>
                  <p className="lp-card-text">{f.text}</p>
                </div>
              )
            })}
          </div>

          <div className="lp-narrow lp-center" style={{ marginTop: 36 }} data-reveal>
            <p className="lp-label">Просто скажи</p>
            <div className="lp-phrases">
              {PHRASES.map(p => <span className="lp-phrase" key={p}>«{p}»</span>)}
            </div>
          </div>
        </Section>

        {/* ── Проактивность (Pro) ── */}
        <Section
          id="proactive"
          label="Тариф Pro"
          title="Дальше Delo работает само"
          lead="Договорились созвониться в четверг? Обещал прислать документы? Замечу в переписке и во входящих — и напомню вовремя."
        >
          <div className="lp-grid lp-grid--3">
            {PROACTIVE.map((f, i) => {
              const Icon = f.icon
              return (
                <div className="lp-card" key={f.title} data-reveal style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="lp-icon"><Icon size={18} color="#5B7FFF" /></div>
                  <h3 className="lp-h3">{f.title}</h3>
                  <p className="lp-card-text">{f.text}</p>
                </div>
              )
            })}
          </div>
          <p className="lp-center lp-muted" style={{ marginTop: 18 }} data-reveal>
            Чтение чатов подключается через Telegram Business — нужен Telegram Premium.
            История переписки до подключения не читается.
          </p>
        </Section>

        {/* ── Приватность ── */}
        <Section
          label="Приватность"
          title="Доступ ровно такой, какой ты дал"
          lead="И его можно забрать в любой момент."
        >
          <div className="lp-grid">
            {PRIVACY_POINTS.map((p, i) => {
              const Icon = p.icon
              return (
                <div className="lp-card" key={p.title} data-reveal style={{ transitionDelay: `${(i % 2) * 70}ms` }}>
                  <div className="lp-icon"><Icon size={18} color="#5B7FFF" /></div>
                  <h3 className="lp-h3">{p.title}</h3>
                  <p className="lp-card-text">{p.text}</p>
                </div>
              )
            })}
          </div>
        </Section>

        {/* ── Тарифы ── */}
        <Section
          id="pricing"
          label="Тарифы"
          title="Начни бесплатно — перейдёшь, когда станет мало"
          lead={`При первом входе даю ${TRIAL_DAYS} дня Pro, чтобы посмотреть на максимум. Карта не нужна.`}
        >
          <div className="lp-center">
            <div className="lp-switch" role="group" aria-label="Период оплаты">
              <button
                className={period === 'month' ? 'is-on' : ''}
                onClick={() => setPeriod('month')}
                aria-pressed={period === 'month'}
              >
                Помесячно
              </button>
              <button
                className={period === 'year' ? 'is-on' : ''}
                onClick={() => setPeriod('year')}
                aria-pressed={period === 'year'}
              >
                На год {pricing.yearlyDiscountLabel && `· ${pricing.yearlyDiscountLabel}`}
              </button>
            </div>
          </div>

          <div className="lp-plans">
            {TIERS.map((tier, i) => {
              const key = period === 'year' ? tier.year : tier.month
              const plan = key ? pricing.plan(key) : undefined
              const isFree = tier.month === null

              let price: ReactNode = <span className="lp-skel" />
              let note = ''
              if (isFree) {
                price = <>{fmtRub(0)}</>
                note = 'Навсегда, без карты'
              } else if (plan) {
                const monthly = period === 'year' ? perMonth(plan.rub) : plan.rub
                price = <>{fmtRub(monthly)} <small>/мес</small></>
                note = period === 'year'
                  ? `${fmtRub(plan.rub)} за год · или ${fmtNum(plan.stars)} Stars`
                  : `или ${fmtNum(plan.stars)} Stars`
              }

              return (
                <div
                  key={tier.id}
                  className={tier.popular ? 'lp-plan lp-plan--popular' : 'lp-plan'}
                  data-reveal
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  {tier.popular && <span className="lp-plan-flag">Максимум возможностей</span>}
                  <div>
                    <div className="lp-plan-name">{tier.name}</div>
                    <div className="lp-plan-tagline">{tier.tagline}</div>
                  </div>
                  <div>
                    <div className="lp-plan-price">{price}</div>
                    <div className="lp-plan-note">{note}</div>
                  </div>
                  <ul className="lp-plan-list">
                    {tier.features.map(f => (
                      <li key={f}><CheckIcon />{f}</li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 'auto', paddingTop: 4 }}>
                    <Cta wide sm={!tier.popular} ghost={isFree}>
                      {isFree ? 'Попробовать' : `Взять ${tier.name}`}
                    </Cta>
                  </div>
                </div>
              )
            })}
          </div>

          <p className="lp-center lp-muted" style={{ marginTop: 20 }} data-reveal>
            Оплата картой или Telegram Stars прямо в чате. Автопродления нет — ничего не спишется само.
            Возврат в течение 14 дней, если подписка не использовалась.
            {pricing.isError && ' Цены сейчас недоступны — актуальные покажет бот.'}
          </p>
        </Section>

        {/* ── FAQ ── */}
        <Section id="faq" label="Вопросы" title="Коротко о главном">
          <div className="lp-narrow lp-faq" data-reveal>
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaq === i
              return (
                <div className="lp-faq-item" key={item.q}>
                  <button
                    className="lp-faq-q"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.q}</span>
                    <ChevronDown size={18} className={isOpen ? 'lp-faq-chevron is-open' : 'lp-faq-chevron'} />
                  </button>
                  {isOpen && <div className="lp-faq-a">{item.a}</div>}
                </div>
              )
            })}
          </div>
        </Section>

        {/* ── Финальный CTA ── */}
        <section className="lp-section">
          <div className="lp-wrap">
            <div className="lp-final" data-reveal>
              <div className="lp-narrow">
                <h2 className="lp-serif lp-h2">Напиши первое дело — дальше я сам</h2>
                <p className="lp-lead" style={{ marginBottom: 26 }}>
                  Одно сообщение боту, минута на настройку — и {TRIAL_DAYS} дня Pro, чтобы попробовать всё.
                </p>
                <Cta>Открыть в Telegram</Cta>
                <p className="lp-cta-note">Бесплатно · Без карты · Данные можно выгрузить и удалить</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── Подвал ── */}
      <footer className="lp-footer">
        <div className="lp-wrap lp-center">
          <div className="lp-footer-row" style={{ marginBottom: 10 }}>
            <Link to="/privacy">Политика конфиденциальности</Link>
            <span>·</span>
            <Link to="/terms">Условия</Link>
            <span>·</span>
            <Link to="/offer">Оферта</Link>
            <span>·</span>
            <a href={SUPPORT_URL}>Поддержка</a>
            <span>·</span>
            <a href={TG_BOT_URL}>@DeloAppBot</a>
          </div>
          <p className="lp-muted">
            Ответы формируются автоматически и могут содержать неточности.
          </p>
        </div>
      </footer>

      {/* ── Липкая кнопка на мобильном ── */}
      <div className={pastHero ? 'lp-sticky is-on' : 'lp-sticky'}>
        <Cta wide>Открыть в Telegram — {TRIAL_DAYS} дня Pro</Cta>
      </div>
    </div>
  )
}
